import React, { FC, HTMLAttributes } from "react";
import { LoaderCircle } from "lucide-react";
import { cn } from "../tailwind-utils";

const skeletonClassName = "animate-pulse rounded-md bg-slate-200";

export const TextSkeleton: FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    aria-hidden
    className={cn(skeletonClassName, "h-4 w-full", className)}
    {...props}
  />
);

export const CardSkeleton: FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    aria-hidden
    className={cn("w-full rounded-lg border border-slate-200 p-4", className)}
    {...props}
  >
    <TextSkeleton className="h-6 w-2/3" />
    <TextSkeleton className="mt-4 w-full" />
    <TextSkeleton className="mt-2 w-5/6" />
  </div>
);

export const PageSkeleton: FC<{
  className?: string;
  cards?: number;
  titleWidthClassName?: string;
  ariaLabel?: string;
  loadingLabel?: string;
}> = ({
  className,
  cards = 3,
  titleWidthClassName = "w-56",
  ariaLabel,
  loadingLabel,
}) => (
  <section
    role="status"
    aria-live="polite"
    aria-label={ariaLabel}
    className={cn("mx-auto w-full max-w-screen-md", className)}
  >
    <div className="mb-6 flex items-center gap-4 border-b p-3">
      <TextSkeleton className={cn("h-8", titleWidthClassName)} />
      <TextSkeleton className="ml-auto h-10 w-24" />
    </div>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {Array.from({ length: cards }).map((_, index) => (
        <CardSkeleton key={index} />
      ))}
    </div>
    {loadingLabel && <span className="sr-only">{loadingLabel}</span>}
  </section>
);

export const InlineSpinner: FC<{
  className?: string;
  label?: string;
  showLabel?: boolean;
  size?: "sm" | "md" | "lg";
}> = ({ className, label, showLabel = false, size = "md" }) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-5 w-5",
    lg: "h-6 w-6",
  };
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn("inline-flex items-center gap-2", className)}
    >
      <LoaderCircle
        aria-hidden
        className={cn("animate-spin text-current", sizeClasses[size])}
      />
      {label &&
        (showLabel ? <span>{label}</span> : <span className="sr-only">{label}</span>)}
    </span>
  );
};

export const ButtonLoading: FC<{ label?: string }> = ({ label }) => (
  <span className="inline-flex items-center gap-2" role="status" aria-live="polite">
    <LoaderCircle aria-hidden className="h-4 w-4 animate-spin" />
    {label && <span>{label}</span>}
  </span>
);

export const UploadProgress: FC<{
  progress: number;
  status: "loading" | "processing" | "success" | "error";
  message?: string;
  className?: string;
  onRetry?: () => void;
  retryLabel?: string;
}> = ({ progress, status, message, className, onRetry, retryLabel }) => {
  const progressValue = Math.max(0, Math.min(progress, 100));
  return (
    <div
      className={cn("rounded-md border bg-white/95 p-2 text-xs shadow-sm", className)}
      role="status"
      aria-live="polite"
      aria-busy={status === "loading" || status === "processing"}
    >
      <div className="mb-1 flex items-center justify-between gap-2 text-slate-700">
        <span>{message}</span>
        {status === "loading" && <span>{progressValue}%</span>}
      </div>
      <div className="h-2 w-full rounded-full bg-slate-200" aria-hidden>
        <div
          className={cn(
            "h-2 rounded-full transition-all",
            status === "error" ? "bg-destructive" : "bg-blue-600"
          )}
          style={{ width: `${status === "processing" ? 100 : progressValue}%` }}
        />
      </div>
      {status === "error" && onRetry && retryLabel && (
        <button
          type="button"
          className="mt-2 text-xs font-semibold text-link hover:underline"
          onClick={onRetry}
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
};

export const LoadingErrorState: FC<{
  className?: string;
  message: string;
  retryLabel?: string;
  onRetry?: () => void;
}> = ({
  className,
  message,
  retryLabel,
  onRetry,
}) => (
  <div className={cn("rounded-md border border-destructive/40 p-4", className)} role="alert">
    <p className="text-sm text-destructive">{message}</p>
    {onRetry && retryLabel && (
      <button
        type="button"
        className="mt-3 rounded-md border border-destructive/50 px-3 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10"
        onClick={onRetry}
      >
        {retryLabel}
      </button>
    )}
  </div>
);

export const Loading: FC<{ className?: string; label?: string }> = ({
  className,
  label,
}) => <InlineSpinner className={className} label={label} />;
